import { useCallback, useEffect, useRef, useState } from "react";
import { type WithRouterProps, withRouter } from "react-router";

import { useDispatch, useSelector } from "metabase/lib/redux";
import { clearAll, getDownloads } from "metabase/redux/downloads";
import useStatusVisibility from "metabase/status/hooks/use-status-visibility";

import { DownloadsStatusLarge } from "../DownloadsStatusLarge";
import { DownloadsStatusSmall } from "../DownloadsStatusSmall";
import { isCompleted, isErrored } from "../utils/downloads";

const DownloadsStatusView = ({
  location,
}: Pick<WithRouterProps, "location">): JSX.Element | null => {
  const [isExpanded, setIsExpanded] = useState(true);
  const downloads = useSelector(getDownloads);
  const dispatch = useDispatch();
  const { pathname } = location;
  const previousPathname = useRef(pathname);
  const hasActiveDownloads =
    downloads.length > 0 && !downloads.every(isCompleted);
  const isVisible = useStatusVisibility(hasActiveDownloads);
  const requiresAttention = downloads.some(
    (download) => download.truncated || isErrored(download),
  );

  const handleDismiss = useCallback(() => {
    dispatch(clearAll());
  }, [dispatch]);

  useEffect(() => {
    if (!isVisible && !hasActiveDownloads && downloads.length > 0) {
      handleDismiss();
    }
  }, [isVisible, handleDismiss, downloads.length, hasActiveDownloads]);

  useEffect(() => {
    if (requiresAttention) {
      setIsExpanded(true);
    }
  }, [requiresAttention]);

  useEffect(() => {
    const hasNavigated = previousPathname.current !== pathname;
    previousPathname.current = pathname;

    if (hasNavigated && downloads.length > 0 && downloads.every(isCompleted)) {
      handleDismiss();
    }
  }, [downloads, handleDismiss, pathname]);

  if (downloads.length === 0) {
    return null;
  }

  return isExpanded ? (
    <DownloadsStatusLarge
      downloads={downloads}
      onDismiss={handleDismiss}
      onCollapse={() => setIsExpanded(false)}
    />
  ) : (
    <DownloadsStatusSmall
      downloads={downloads}
      onExpand={() => setIsExpanded(true)}
    />
  );
};

export const DownloadsStatus = withRouter(DownloadsStatusView);
